import type { MantineColor } from '@mantine/core';
import {
  ONE_ON_ONE_TYPES,
  ONE_ON_ONE_TYPE_LABELS,
  type OneOnOneType,
} from '../types';

export interface OneOnOneTypeConfig {
  chipLabel: string;
  fullLabel: string;
  /**
   * User-facing label used in calendar invite titles, session reminder /
   * survey emails, and the Calendly scheduling modal (MED-692 / E22-5f).
   * Differs from `fullLabel` which is reserved for admin UI.
   */
  displayLabel: string;
  description: string;
  badgeColor: MantineColor;
  badgeVariant: 'light' | 'outline' | 'filled' | 'dot';
  isStandard: boolean;
  /**
   * MED-696 AC6b: true for the three debrief variants. Drives "Debrief Notes"
   * vs "Session Notes" labeling on the 1:1 recap form.
   */
  isDebrief: boolean;
}

export const ONE_ON_ONE_TYPE_CONFIG: Record<OneOnOneType, OneOnOneTypeConfig> = {
  standard_1_1: {
    chipLabel: '1:1',
    fullLabel: ONE_ON_ONE_TYPE_LABELS.standard_1_1,
    displayLabel: '1:1 Coaching',
    description: 'Standard one-on-one coaching session',
    badgeColor: 'gray',
    badgeVariant: 'light',
    isStandard: true,
    isDebrief: false,
  },
  two_on_one: {
    chipLabel: '2:1',
    fullLabel: ONE_ON_ONE_TYPE_LABELS.two_on_one,
    displayLabel: '2:1 Coaching (with Manager)',
    description: 'Session includes the participant\u2019s manager',
    badgeColor: 'surveyPurple',
    badgeVariant: 'light',
    isStandard: false,
    isDebrief: false,
  },
  '360_debrief': {
    chipLabel: '360',
    fullLabel: ONE_ON_ONE_TYPE_LABELS['360_debrief'],
    displayLabel: '360 Debrief',
    description: 'Debrief of 360 feedback \u2014 have your 360 results ready',
    badgeColor: 'medleyGreen',
    badgeVariant: 'light',
    isStandard: false,
    isDebrief: true,
  },
  hogan_debrief: {
    chipLabel: 'Hogan',
    fullLabel: ONE_ON_ONE_TYPE_LABELS.hogan_debrief,
    displayLabel: 'Hogan Debrief',
    description: 'Debrief of Hogan assessment results',
    badgeColor: 'warningAmber',
    badgeVariant: 'light',
    isStandard: false,
    isDebrief: true,
  },
  other_assessment_debrief: {
    chipLabel: 'Debrief',
    fullLabel: ONE_ON_ONE_TYPE_LABELS.other_assessment_debrief,
    displayLabel: 'Assessment Debrief',
    description: 'Debrief of another assessment',
    badgeColor: 'groupBeta',
    badgeVariant: 'light',
    isStandard: false,
    isDebrief: true,
  },
  // MED-1350: check-ins are unpaid 1:1 rows. Never in a type picker, because
  // the pickers map ONE_ON_ONE_TYPES. Use isCheckinOneOnOneType to test a type.
  intro_checkin: {
    chipLabel: 'Intro',
    fullLabel: ONE_ON_ONE_TYPE_LABELS.intro_checkin,
    displayLabel: 'Intro check-in',
    description: 'Intro check-in with the coach. Not paid.',
    badgeColor: 'gray',
    badgeVariant: 'light',
    isStandard: false,
    isDebrief: false,
  },
  midpoint_checkin: {
    chipLabel: 'Midpoint',
    fullLabel: ONE_ON_ONE_TYPE_LABELS.midpoint_checkin,
    displayLabel: 'Midpoint check-in',
    description: 'Midpoint check-in with the coach. Not paid.',
    badgeColor: 'gray',
    badgeVariant: 'light',
    isStandard: false,
    isDebrief: false,
  },
};

export const ONE_ON_ONE_TYPE_FILTER_OPTIONS: { value: OneOnOneType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  ...ONE_ON_ONE_TYPES.map((type) => ({
    value: type,
    label: ONE_ON_ONE_TYPE_CONFIG[type].fullLabel,
  })),
];

/**
 * MED-1350: an unknown type (a value newer than this bundle, such as a type
 * the API added after an old bundle loaded) falls back to the standard 1:1
 * config. Returning undefined crashed old bundles on the first field read.
 */
export function getOneOnOneTypeConfig(type: OneOnOneType | null | undefined): OneOnOneTypeConfig {
  return (
    (ONE_ON_ONE_TYPE_CONFIG as Partial<Record<string, OneOnOneTypeConfig>>)[
      type ?? 'standard_1_1'
    ] ?? ONE_ON_ONE_TYPE_CONFIG.standard_1_1
  );
}

/**
 * Build a "{DisplayLabel} — Session {N} of {M}" title used in the Calendly
 * scheduling modal and session-related communications (MED-692 / E22-5f).
 * Falls back to the bare label when session counts aren't available.
 */
export function buildOneOnOneSessionTitle(
  type: OneOnOneType | null | undefined,
  sessionNumber: number | null | undefined,
  totalSessions?: number | null,
): string {
  const label = getOneOnOneTypeConfig(type).displayLabel;
  if (!sessionNumber) return label;
  const suffix =
    totalSessions && totalSessions > 0
      ? `Session ${sessionNumber} of ${totalSessions}`
      : `Session ${sessionNumber}`;
  return `${label} \u2014 ${suffix}`;
}
